<template>
  <div class="flex flex-col gap-1 text-left">
    <p v-if="label" class="text-sm font-bold text-neutral50 m-0">{{ label }}</p>
    <div
      class="inline-flex w-fit rounded-lg border border-gray-200 bg-gray-50 p-0.5"
      role="group"
      :aria-label="label || undefined"
    >
      <button
        v-for="option in options"
        :key="String(option.value)"
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap"
        :class="
          option.value === modelValue
            ? 'bg-white text-primary30 font-bold shadow-sm'
            : 'text-neutral50 hover:text-primary30'
        "
        :data-testid="optionTestId(option.value)"
        :aria-pressed="option.value === modelValue ? 'true' : 'false'"
        @click="$emit('update:modelValue', option.value)"
      >
        {{ option.label }}
        <span
          v-if="option.count !== undefined"
          :class="option.value === modelValue ? 'text-primary30 font-semibold' : 'text-gray-400'"
          >{{ option.count }}</span
        >
      </button>
    </div>
    <p v-if="help" class="text-xs text-neutral50 m-0">{{ help }}</p>
  </div>
</template>

<script setup lang="ts" generic="T extends string">
/**
 * Segmentado de duas ou três opções mutuamente exclusivas, como nos mockups do Eixo 3 da
 * discovery de auditoria: a escolha inteira fica visível, ao contrário de um `v-select`, o que
 * importa quando as opções mudam o processo (quem confere a folha, aprovar sozinho ou não).
 *
 * `aria-pressed` em vez de `role="radio"`: são botões de ação imediata sobre um valor único, e
 * é o atributo que o teste lê para saber qual está ativo.
 */

interface SegmentedOption<V extends string> {
  label: string
  value: V
  /** Contador opcional exibido ao lado do label (ex: quantidade de itens da opção). */
  count?: number
}

const props = defineProps<{
  modelValue: T | null
  options: SegmentedOption<T>[]
  label?: string
  help?: string
  /** Prefixo do `data-testid` de cada opção — sem ele as opções não são endereçáveis. */
  testIdPrefix?: string
}>()

defineEmits<{
  (e: 'update:modelValue', value: T): void
}>()

function optionTestId(value: T): string | undefined {
  if (!props.testIdPrefix) return undefined
  return `${props.testIdPrefix}-${String(value).toLowerCase()}`
}
</script>
